<template>
    <div>
        <v-card class="tax-rates" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('tax_rates.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            v-if="allowUpdates"
                            class="minw100"
                            color="white"
                            outlined
                            @click="openTaxRateForm()"
                        >
                            <v-icon left>fal fa-plus</v-icon>
                            {{ $t('general.add') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <v-data-table
                    class="operations striped"
                    :headers="headers"
                    :items="taxRates"
                    :mobile-breakpoint="0"
                    :items-per-page="20"
                    hide-default-footer
                >
                    <template slot="item" slot-scope="props">
                        <tr>
                            <td class="minw150">
                                <span v-if="props.item.name">{{ props.item.name }} ({{ props.item.label }})</span>
                                <span v-else>{{ props.item.label }}</span>
                            </td>
                            <td>
                                {{ props.item.type === 'TaxOnNet' || props.item.type === 'product' ? $t('tax_rates.applies_to_product') : $t('tax_rates.applies_to_order') }}
                            </td>
                            <td>{{ props.item.rate }}%</td>
                            <td align="center" class="w35 maxw60">
                                <v-menu
                                    v-if="allowUpdates"
                                    bottom
                                    transition="slide-y-transition"
                                >
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item
                                            :key="1"
                                            @click="openTaxRateForm('edit', props.item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item
                                            :key="2"
                                            @click="deleteTaxRate(props.item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon class="error--text">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title class="error--text">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>

                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <cod-dialog id="viewTaxRateForm" :show.sync="formControl.visible">
            <!-- Header -->
            <template slot="header">
                <span v-if="formControl.mode == 'add'">{{ $t('tax_rates.add') }}</span>
                <span v-if="formControl.mode == 'edit'">{{ $t('tax_rates.edit') }}</span>
            </template>

            <!-- Content -->
            <div class="pa-5" slot="content">
                <v-form ref="taxRateForm" v-model="form.valid">
                    <div class="form-container">
                        <v-text-field
                            ref="labelField"
                            v-model="form.label"
                            outlined
                            :label="$t('tax_rates.label')"
                            :rules="form.rules.label"
                            :error-messages="form.errors.label"
                            validate-on-blur
                        />

                        <number-field
                            v-model="form.rate"
                            :label="$t('tax_rates.rate')"
                            :rules="form.rules.rate"
                            :error-messages="form.errors.rate"
                            append-icon="fal fa-percent"
                            outlined
                            validate-on-blur
                        />

                        <div class="pa-1">{{ $t('tax_rates.applies_to') }}</div>
                        <v-btn-toggle class="flex" color="primary" v-model="form.type" mandatory>
                            <v-btn
                                class="flex-1"
                                text
                                value="product"
                            >
                                {{ $t('tax_rates.applies_to_product') }}
                            </v-btn>
                            <v-btn
                                class="flex-1"
                                text
                                color="warning"
                                value="order"
                            >
                                {{ $t('tax_rates.applies_to_order') }}
                            </v-btn>
                        </v-btn-toggle>

                        <v-alert
                            class="mt-5"
                            prominent
                            :color="form.type === 'TaxOnNet' ? 'primary' : 'warning'"
                            type="info"
                            text
                        >
                            <span v-if="form.type === 'TaxOnNet'">{{ $t('tax_rates.applies_to_product_info') }}</span>
                            <span v-else>{{ $t('tax_rates.applies_to_order_info') }}</span>
                        </v-alert>
                    </div>
                </v-form>
            </div>

            <!-- Footer -->
            <template slot="footer">
                <v-btn
                    class="minw100 mr-1 flex-1"
                    outlined
                    color="primary"
                    @click="formControl.visible = false"
                >
                    {{ $t('general.cancel') }}
                </v-btn>
                <v-btn
                    color="primary"
                    depressed
                    :disabled="form.busy || !form.valid"
                    :loading="form.busy"
                    class="minw100 ml-1 flex-1"
                    @click="saveTaxRate()"
                >
                    {{ $t('general.save') }}
                </v-btn>
            </template>

        </cod-dialog>

    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import CodDialog from '@/components/CodDialog';
import NumberField from '@/components/NumberField';
import ApiService from '@/services/ApiService';

export default {
    components: {
        CodDialog,
        NumberField
    },

    created() {
        loadData('/admin/load/tax-rates').then(data => this.onDataLoaded(data));
    },

    data() {
        return {
            taxRates: [],

            form: this.createForm(),
            formControl: {
                mode: 'add',
                visible: false,
            },

            headers: [
                { text: this.$t('tax_rates.label'), value: 'label' },
                { text: this.$t('tax_rates.applies_to'), value: 'type' },
                { text: this.$t('tax_rates.rate'), value: 'rate' },
                { text: '', sortable: false }
            ],
        };
    },

    computed: {
        /**
         * Check if we should allow updates
         */
        allowUpdates() {
            return this.tenant.venue.settings.update_tax_rates_enabled
        }
    },

    methods: {
        /*
        * Place loaded data into adequate variables
        */
        onDataLoaded(data) {
            this.taxRates = data.taxRates;
        },

        /**
         * Open taxRate form to add or edit a taxRate
         * @param taxRate
         */
        openTaxRateForm(mode = 'add', taxRate = null) {
            this.form = this.createForm(taxRate);
            this.formControl.mode = mode;
            this.formControl.visible = true;

            this.$nextTick(() => {
                this.$nextTick(() => {
                    if(this.$refs.taxRateForm) this.$refs.taxRateForm.resetValidation();

                    if (mode !== 'edit') {
                        this.$refs.labelField.focus();
                    }
                });
            });
        },

        /**
         * Create taxRate form
         * @param taxRate
         */
        createForm(taxRate = null) {
            return new CodForm({
                id: taxRate ? taxRate.id : null,
                label: taxRate ? taxRate.label : null,
                type: taxRate ? taxRate.type : null,
                rate: taxRate ? parseFloat(taxRate.rate) : null,

                rules: {
                    label: [CodForm.rule('required')],
                    type: [CodForm.rule('required')],
                    rate: [CodForm.rule('required'), CodForm.rule('max_value', 99)],
                },

                valid: false
            });
        },

        /**
         * Save TaxRate
         */
        saveTaxRate() {
            if (!this.form.valid) return false;

            if (this.formControl.mode !== 'edit') {
                ApiService.post('/admin/tax-rates', this.form).then(response => {
                    this.formControl.visible = false;
                    this.taxRates.push(response.data);
                    this.$snackbar(this.$t('tax_rates.saved'), 'success');
                })
            } else {
                ApiService.patch(`/admin/tax-rates/${this.form.id}`, this.form).then(response => {
                    this.formControl.visible = false;

                    this.taxRates.forEach((item, index, array) => {
                        if (item.id === this.form.id) {
                            this.$set(array, index, response.data);
                        }
                    });
                    this.$snackbar(this.$t('tax_rates.updated'), 'success');
                })
            }
        },

        /**
         * Delete TaxRate
         * @param taxRate
         */
        deleteTaxRate(taxRate) {
            this.$confirm(this.$t('tax_rates.permanently_delete'), {
                buttonTrueText: this.$t('general.confirm'),
                icon: 'fal fa-times',
                color: 'error',
                title: this.$t('general.delete')
            }, result => {
                if (!result) return;

                this.formControl.visible = false;
                ApiService.delete(`/admin/tax-rates/${taxRate.id}`, {}).then(response => {
                    this.taxRates = this.taxRates.filter(item => item.id !== taxRate.id);
                    this.$snackbar(this.$t('tax_rates.deleted'), 'success');
                })
            });
        },
    }
};
</script>

<style lang="scss" scoped>
.tax-rates {
    max-width: 600px;

    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }
}

.form-container {
    min-height: 450px;
}
</style>
